/**
 * Time-saved time-series hook (v1.4 dashboard SSOT).
 *
 * Thin TanStack Query wrapper around `getTimeSavedSeries`. Consumed by
 * `<TimeSavedChart>` on the Overview tab (rendered by Agent G's component
 * work). Cache key includes `granularity` so the 'auto' bucket size and an
 * explicit 'daily' selection don't collide.
 *
 * `staleTime: 60_000` matches `useOverviewKpis` — the chart and KPI card
 * refresh in lock-step.
 */

import { useQuery } from '@tanstack/react-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import {
  getTimeSavedBreakdown,
  getTimeSavedBreakdownItems,
  getTimeSavedSeries,
} from '@/services/api-client';
import type {
  TimeSavedBreakdownItemsResponse,
  TimeSavedBreakdownResponse,
  TimeSavedSeries,
} from '@/services/api-types';

const STALE_TIME = 60_000;

export function useTimeSavedSeries(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useQuery<TimeSavedSeries>({
    queryKey: [
      'v2',
      'operations',
      'time-saved-series',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () =>
      (await getTimeSavedSeries(filters, granularity)).data,
    staleTime: STALE_TIME,
  });
}

/**
 * Per-bucket drill-down for the Time-saved chart (Task 3 — v1.4
 * dashboard-ux-fix). Returns the list of users who contributed to the
 * minutes-saved value of a single clicked bucket.
 *
 * Enabled only when `bucket !== null` — the typical pattern is that
 * OverviewPage lifts the click into a `selectedBucket` state and passes it
 * through here; when the modal is closed (`null`), the query is inert
 * (TanStack Query's `enabled: false`) so we don't hit the backend.
 *
 * Cache key carries `bucket + granularity + filters` so two buckets from
 * the same window are cached independently and granularity changes
 * invalidate cleanly.
 */
export function useTimeSavedBreakdown(
  filters: Filters,
  bucket: string | null,
  granularity: Filters['granularity'],
) {
  return useQuery<TimeSavedBreakdownResponse>({
    queryKey: [
      'v2',
      'overview',
      'time-saved-breakdown',
      bucket,
      granularity,
      filtersQuestion(filters),
    ],
    enabled: bucket !== null,
    queryFn: async () => {
      // Unreachable when `enabled=false`, but TS wants the narrowing.
      if (!bucket) throw new Error('bucket required');
      return (await getTimeSavedBreakdown(filters, bucket, granularity)).data;
    },
    staleTime: STALE_TIME,
  });
}

/**
 * Per-trace drill-down for the Time-saved chart — feeds the sortable details
 * table the user lands on after clicking a bar. Same enable/disable contract
 * as `useTimeSavedBreakdown`; cache key keyed on bucket + granularity +
 * filters.
 */
export function useTimeSavedBreakdownItems(
  filters: Filters,
  bucket: string | null,
  granularity: Filters['granularity'],
) {
  return useQuery<TimeSavedBreakdownItemsResponse>({
    queryKey: [
      'v2',
      'overview',
      'time-saved-breakdown-items',
      bucket,
      granularity,
      filtersQuestion(filters),
    ],
    enabled: bucket !== null,
    queryFn: async () => {
      if (!bucket) throw new Error('bucket required');
      return (await getTimeSavedBreakdownItems(filters, bucket, granularity))
        .data;
    },
    staleTime: STALE_TIME,
  });
}
